import { parseError } from './parseError';
import { sleep } from './sleep';

export interface WaitForOptions<T> {
  timeoutMs?: number;
  intervalMs?: number;
  /* Clock. Use Date.now or performance.now for sub-ms precision. Defaults to Date.now */
  clockMs?: () => number;
  /** Sleeping function. Defaults to setTimeout. */
  sleepMs?: (amount: number) => Promise<void>;
  /**
   * Test for the validity of a value. If this is provided and fails on a value, The waiter will
   * retry.
   */
  valueTest?: (item: T) => boolean;
  /** Callback if an error occurs when the value is read. Optional. */
  onAttemptError?: (error: Error) => void;
  /** Callback if the wait exceeds. Throws an error if this isn't specified. */
  onTimeout?: (error?: Error) => void;
}

/**
 * Waits for an attempt to return without throwing an error and return a value that passes a value
 * check.
 *
 * Retries every intervalMs until timeoutMs at which point it throws an error or returns a null if
 * onTimeout callback is provided.
 */
export async function waitFor<T>(
  attempt: () => Promise<T>,
  options: WaitForOptions<T> = {}
): Promise<T | null> {
  const {
    timeoutMs = 5e3,
    intervalMs = 100,
    clockMs = Date.now,
    sleepMs = sleep,
    valueTest,
    onAttemptError: onEveryError,
    onTimeout,
  } = options;
  const startMs = clockMs();

  let lastError: Error | undefined;

  while (clockMs() < startMs + timeoutMs) {
    try {
      const latest = await attempt();

      if (!valueTest || valueTest(latest)) {
        return latest;
      }
    } catch (error) {
      lastError = parseError(error);
      onEveryError?.(lastError);
    }

    // Wait until next attempt.
    await sleepMs(intervalMs);
  }

  if (!onTimeout) {
    if (!lastError) lastError = new Error(`Timeout of ${(timeoutMs / 1e3).toFixed(1)}s exceeded`);
    throw lastError;
  }
  onTimeout(lastError);
  return null;
}
